import { useMutation } from '@apollo/client/react'
import {
  Checkbox,
  ConfirmationDialog,
  FailureNotice,
  FieldMessage,
  InlineConfirmation,
  useTranslate,
} from '@athena/ui'
import { useRef, useState } from 'react'

import {
  AssignPracticeDocument,
  PracticeDocument,
  UnassignPracticeDocument,
  type StudentFieldsFragment,
} from '../../graphql/generated/operations.js'
import { useUserErrorText } from '../../lib/user-errors.js'

export interface AssignPanelProps {
  practiceId: string
  /** Every student this tutor has. */
  students: readonly StudentFieldsFragment[]
  assignedIds: ReadonlySet<string>
  /** False until something is published, since there is nothing to attempt. */
  canAssign: boolean
}

type Change = 'assign' | 'remove'

/**
 * What the last change said: one of these at a time, so a confirmation of an
 * earlier change never sits beside the failure of a later one.
 */
type Feedback =
  | { kind: 'done'; text: string }
  | { kind: 'refused'; text: string }
  | {
      kind: 'failed'
      title: string
      error: unknown
      student: StudentFieldsFragment
      change: Change
    }

/**
 * Assign a practice to students (handoff, Interactions: "Assignment to one or
 * more students can happen at publish time or later").
 *
 * Unassigning goes through a confirmation, because it reaches the student at
 * once and the consequence is not obvious: the practice leaves their list, their
 * scores stay in their history, and assigning it again gives it back. The dialog
 * says exactly that rather than "this cannot be undone" — and, since nothing is
 * lost for good, it is `caution`, not red, with "Keep assigned" recommended. It
 * stays open until the server has answered, and says a failure inside itself.
 * Assigning asks nothing: it is immediate and undone by unticking.
 *
 * Every change is confirmed only once the server has said so, and a change
 * the server did not make leaves the checkbox where the server has it: the
 * boxes are the assignments as they are, never as they were asked for. A
 * refusal the server explains is said in its words; a request that never got
 * an answer says so and offers to try again — which is safe, because
 * assigning an assigned student and removing a removed one both change
 * nothing.
 */
export function AssignPanel({ practiceId, students, assignedIds, canAssign }: AssignPanelProps) {
  const t = useTranslate()
  const userErrorText = useUserErrorText()
  const refetch = { refetchQueries: [PracticeDocument], awaitRefetchQueries: true }
  const [assign] = useMutation(AssignPracticeDocument, refetch)
  const [unassign] = useMutation(UnassignPracticeDocument, refetch)

  const [pendingRemoval, setPendingRemoval] = useState<StudentFieldsFragment>()
  const [feedback, setFeedback] = useState<Feedback>()
  /** Why the removal being confirmed did not go through, said inside its dialog. */
  const [removalFailure, setRemovalFailure] = useState<Feedback>()
  /** The students with a change on its way: their box waits, so one click is one request. */
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())
  const inFlight = useRef(new Set<string>())

  /**
   * Make one change, and say what it came to. Resolves true only when the
   * server made it.
   *
   * `retry`: the failure stays on screen, saying "Trying again…", until the
   * answer replaces it. `inDialog`: a removal being confirmed, whose refusal
   * or failure is said inside the dialog rather than under the list.
   */
  async function change(
    student: StudentFieldsFragment,
    what: Change,
    { retry = false, inDialog = false }: { retry?: boolean; inDialog?: boolean } = {},
  ): Promise<boolean> {
    if (inFlight.current.has(student.id)) return false
    inFlight.current.add(student.id)
    setBusy(new Set(inFlight.current))
    if (!retry) setFeedback(undefined)
    setRemovalFailure(undefined)
    const report = inDialog ? setRemovalFailure : setFeedback

    try {
      const variables = { practiceId, studentId: student.id }
      const payload =
        what === 'assign'
          ? (await assign({ variables })).data?.assignPractice
          : (await unassign({ variables })).data?.unassignPractice

      if (!payload?.practice) {
        const refusal = payload?.userErrors[0]
        report({
          kind: 'refused',
          text: refusal
            ? userErrorText(refusal)
            : t(
                what === 'assign' ? 'practice.assign.assignFailed' : 'practice.assign.removeFailed',
                [student.displayName],
              ),
        })
        return false
      }
      setFeedback({
        kind: 'done',
        text: t(what === 'assign' ? 'practice.assign.assigned' : 'practice.assign.removed', [
          student.displayName,
        ]),
      })
      return true
    } catch (error) {
      report({
        kind: 'failed',
        title: t(
          what === 'assign' ? 'practice.assign.assignFailed' : 'practice.assign.removeFailed',
          [student.displayName],
        ),
        error,
        student,
        change: what,
      })
      return false
    } finally {
      inFlight.current.delete(student.id)
      setBusy(new Set(inFlight.current))
    }
  }

  if (students.length === 0) {
    return <p className="text-body text-text-secondary">{t('practice.assign.addStudentFirst')}</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {students.map((student) => (
        <Checkbox
          key={student.id}
          label={student.displayName}
          checked={assignedIds.has(student.id)}
          disabled={(!canAssign && !assignedIds.has(student.id)) || busy.has(student.id)}
          onCheckedChange={(checked) => {
            if (checked === true) {
              void change(student, 'assign')
            } else {
              setPendingRemoval(student)
            }
          }}
        />
      ))}

      {!canAssign ? <FieldMessage>{t('practice.assign.publishFirst')}</FieldMessage> : null}
      {feedback?.kind === 'refused' ? (
        <FieldMessage tone="error">{feedback.text}</FieldMessage>
      ) : null}
      {feedback?.kind === 'done' ? <InlineConfirmation>{feedback.text}</InlineConfirmation> : null}
      {feedback?.kind === 'failed' ? (
        <FailureNotice
          title={feedback.title}
          error={feedback.error}
          retrying={busy.has(feedback.student.id)}
          onRetry={() => void change(feedback.student, feedback.change, { retry: true })}
        />
      ) : null}

      <ConfirmationDialog
        open={pendingRemoval !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRemoval(undefined)
            setRemovalFailure(undefined)
          }
        }}
        intent="caution"
        title={
          pendingRemoval
            ? t('practice.assign.removeTitle', [pendingRemoval.displayName])
            : t('practice.assign.removeTitleFallback')
        }
        description={
          pendingRemoval
            ? t('practice.assign.removeConsequence', [pendingRemoval.displayName])
            : t('practice.assign.removeConsequenceFallback')
        }
        confirmLabel={
          pendingRemoval
            ? t('practice.assign.removeFrom', [pendingRemoval.displayName])
            : t('practice.assign.removeFallback')
        }
        confirmingLabel={t('practice.assign.removing')}
        cancelLabel={t('practice.assign.keep')}
        failure={
          removalFailure?.kind === 'refused' ? (
            <FieldMessage tone="error">{removalFailure.text}</FieldMessage>
          ) : removalFailure?.kind === 'failed' ? (
            // Pressing "Remove" again is the retry.
            <FailureNotice title={removalFailure.title} error={removalFailure.error} />
          ) : null
        }
        onConfirm={async () => {
          if (!pendingRemoval) return true
          const done = await change(pendingRemoval, 'remove', { inDialog: true })
          if (done) setPendingRemoval(undefined)
          return done
        }}
      />
    </div>
  )
}
